import { createSelector } from "@reduxjs/toolkit"
import PropTypes from "prop-types"
import { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import withTotalItemPrice from "areas/product/hoc/with-total-item-price"
import { addToCart, setFixedATC } from "areas/product/store/actions"
import Button from "components/ui/button"
import { jewlr_t } from "helpers/application"
import { to_currency } from "helpers/number"
import useWindowDimensions from "helpers/window-dimensions"
import { cn } from "styles/utils"

import PreviewThumbnail from "./preview-thumbnail"

const selectStateProps = createSelector(
  [(state) => state.wizard, (state) => state.wizardUI],
  (wizard, wizardUI) => ({
    fixedATC: wizardUI.fixedATC,
    isAddingToCart: wizardUI.isAddingToCart,
    lid: wizardUI.lid,
    olid: wizard.olid,
    prefStore: wizardUI.prefStore,
    wizardLoading: wizard.loading,
  })
)

const AddToCartButton = ({
  imageAreaRef,
  previewImage,
  totalItemPrice,
  // totalOriginalPrice,
  // wizardRef,
}) => {
  const { isMobile } = useWindowDimensions()
  const inPageATCRef = useRef()

  const dispatch = useDispatch()
  const { fixedATC, isAddingToCart, lid, olid, prefStore, wizardLoading } =
    useSelector(selectStateProps)

  // const unifiedStonePickerExp = useSelector(
  //   (state) => state.experiment.unifiedStonePicker.variant_id
  // )

  const cartStr = jewlr_t("Cart")
  const cta = olid
    ? "Update Order"
    : lid
      ? isAddingToCart
        ? `Updating ${cartStr}...`
        : `Update ${cartStr}`
      : isAddingToCart
        ? `Adding to ${cartStr}...`
        : `Add to ${cartStr}`

  const showThumbnail = isMobile && previewImage

  const handleAddToCart = useCallback(
    (e) => {
      e.preventDefault()
      if (!isAddingToCart) {
        dispatch(addToCart())
      }
    },
    [dispatch, isAddingToCart]
  )

  useEffect(() => {
    if (!isMobile) {
      dispatch(setFixedATC(false))
      return
    }

    const imageArea = imageAreaRef.current
    const inPageATC = inPageATCRef.current

    if (!imageArea || !inPageATC) return

    let observedEl = imageArea
    // let observedEl = unifiedStonePickerExp === 1 ? imageArea : inPageATC

    const observer = new IntersectionObserver(
      ([entry]) => {
        const elInView = entry.isIntersecting
        // unifiedStonePickerExp === 1
        // ? entry.isIntersecting
        // : entry.boundingClientRect.bottom > 0

        dispatch(setFixedATC(!elInView))
      },
      {
        // threshold: unifiedStonePickerExp === 1 ? 0.4 : 0,
        threshold: 0.4,
      }
    )

    observer.observe(observedEl)

    return () => observer.disconnect()
    // }, [dispatch, isMobile, unifiedStonePickerExp])
  }, [dispatch, isMobile])

  // useLayoutEffect(() => {
  //   const addToCartScrollTick = () => {
  //     if (!cancelled.current) {
  //       if (isMobile) {
  //         window.requestAnimationFrame(() => {
  //           if (wizardRef.current) {
  //             const rect = wizardRef.current.getBoundingClientRect()
  //             if (!rect) return

  //             setFixedATC(rect.bottom <= 0)
  //           }
  //         })
  //       } else {
  //         setFixedATC(false)
  //       }
  //     }
  //   }

  //   addToCartScrollTick()
  //   window.addEventListener("scroll", addToCartScrollTick, false)
  //   return () =>
  //     window.removeEventListener("scroll", addToCartScrollTick, false)
  // }, [isMobile, wizardRef.current])

  return (
    <>
      {fixedATC && (
        <div
          className={cn(
            "tablet:shadow-none tablet:static tablet:w-auto tablet:p-0 shadow-(--box-shadow) fixed bottom-0 left-0 z-30 w-full bg-white",
            showThumbnail ? "flex items-center gap-12" : "p-16"
          )}
          style={{ "--box-shadow": "0 -4px 4px 0 rgba(0, 0, 0, 0.15)" }}
        >
          {showThumbnail && <PreviewThumbnail previewImage={previewImage} />}
          <div className={cn("flex-1", showThumbnail && "py-16 pr-20")}>
            <Button
              aria-label={cta}
              className="js-add-cart text-18 leading-20 w-full flex-1"
              data-ge-add-to-cart
              disabled={!prefStore || isAddingToCart || wizardLoading}
              loading={isAddingToCart}
              onClick={handleAddToCart}
              type="submit"
              variant="secondary"
            >
              {totalItemPrice && (
                <>
                  <span className="mr-4" data-ge-parentproduct-total-price>
                    {to_currency(totalItemPrice)}
                  </span>
                  {" \u2013 "}
                </>
              )}
              {cta}
            </Button>
          </div>
        </div>
      )}
      <Button
        aria-label={cta}
        className="js-add-cart text-18 leading-20 w-full flex-1"
        data-cy="product-wizard-add-to-cart-btn"
        data-ge-add-to-cart
        disabled={!prefStore || isAddingToCart || wizardLoading}
        loading={isAddingToCart}
        onClick={handleAddToCart}
        ref={inPageATCRef}
        type="submit"
        variant="secondary"
      >
        {totalItemPrice && (
          <>
            <span className="mr-4" data-ge-parentproduct-total-price>
              {to_currency(totalItemPrice)}
            </span>{" "}
            &ndash;{" "}
          </>
        )}
        {cta}
      </Button>
    </>
  )
}

AddToCartButton.propTypes = {
  imageAreaRef: PropTypes.object,
  previewImage: PropTypes.object,
  totalItemPrice: PropTypes.number,
  // totalOriginalPrice: PropTypes.number,
  // wizardRef: PropTypes.object,
}

export default withTotalItemPrice(AddToCartButton)
